import React, { useState, useEffect } from "react";
import axios from "axios";
import "../css/theme.css";
import "../css/addCreditor.css";

const UpcomingTasks = ({ debitors }) => {
  const [tasks, setTasks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [savingStatusId, setSavingStatusId] = useState(null); // track which row is saving

  const [newTask, setNewTask] = useState({
    debitor_id: "",
    promise_amount: "",
    promise_date: "",
    task_details: "",
    total_dues: "",
    demand_amount: "",
    oldest_dues: "",
    nature_of_party: "",
    reminders: "",
    bank_account: "",
    payment_options: "",
    status: "Pending",
  });

  const resetTask = () => {
    setNewTask({
      debitor_id: "",
      promise_amount: "",
      promise_date: "",
      task_details: "",
      total_dues: "",
      demand_amount: "",
      oldest_dues: "",
      nature_of_party: "",
      reminders: "",
      bank_account: "",
      payment_options: "",
      status: "Pending",
    });
    setError("");
    setSuccess("");
  };

  const fetchTasks = async () => {
    try {
      const res = await axios.get("/api/get_tasks.php");
      setTasks(res.data || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      alert("Error loading tasks from server.");
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const validateTask = () => {
    if (!newTask.debitor_id) {
      setError("Please select a debtor.");
      return false;
    }

    if (
      !newTask.promise_amount ||
      isNaN(newTask.promise_amount) ||
      Number(newTask.promise_amount) <= 0
    ) {
      setError("Promise amount must be a positive number.");
      return false;
    }

    if (!newTask.promise_date) {
      setError("Promise date is required.");
      return false;
    }

    if (
      newTask.total_dues &&
      (isNaN(newTask.total_dues) || Number(newTask.total_dues) < 0)
    ) {
      setError("Total dues must be a non-negative number.");
      return false;
    }

    if (
      newTask.demand_amount &&
      (isNaN(newTask.demand_amount) || Number(newTask.demand_amount) < 0)
    ) {
      setError("Demand amount must be a non-negative number.");
      return false;
    }

    if (newTask.bank_account) {
      const accPattern = /^[0-9]{6,20}$/;
      if (!accPattern.test(newTask.bank_account.trim())) {
        setError("Bank account number should be 6–20 digits.");
        return false;
      }
    }

    setError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateTask()) return;

    const payload = {
      ...newTask,
      promise_amount: Number(newTask.promise_amount),
      total_dues: newTask.total_dues ? Number(newTask.total_dues) : null,
      demand_amount: newTask.demand_amount ? Number(newTask.demand_amount) : null,
      bank_account: newTask.bank_account.trim() || null,
      task_details: newTask.task_details || "",
    };

    try {
      const res = await axios.post("/api/add_task.php", payload);
      console.log("add_task.php response:", res.data);

      if (res.data && res.data.success) {
        setSuccess("Task added successfully!");
        setTimeout(() => setSuccess(""), 2000);
        setShowModal(false);
        resetTask();
        fetchTasks();
      } else {
        setError(res.data?.message || "Failed to add task.");
      }
    } catch (error) {
      console.error("Error calling add_task.php:", error);
      setError("Error talking to server. Please try again.");
    }
  };

  const handleInlineStatusChange = async (taskId, newStatus) => {
    const oldTasks = [...tasks];
    setSavingStatusId(taskId);

    // optimistic UI update
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskId ? { ...t, status: newStatus } : t
      )
    );

    try {
      const res = await axios.post("/api/update_task_status.php", {
        id: taskId,
        status: newStatus,
      });

      if (!res.data || !res.data.success) {
        throw new Error(res.data?.message || "Server rejected update");
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      alert("Failed to update status. Reverting.");
      setTasks(oldTasks); // revert on error
    } finally {
      setSavingStatusId(null);
    }
  };

  return (
    <div className="ac-container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 ac-title">Upcoming Tasks</h4>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetTask();
            setShowModal(true);
          }}
        >
          + Add Task
        </button>
      </div>

      {/* Tasks Table */}
      <div className="table-responsive">
        <table className="table table-bordered w-100">
          <thead className="table">
            <tr>
              <th>Debtor Name</th>
              <th>Date</th>
              <th>Promise to Pay</th>
              <th>Total Dues</th>
              <th>Demand Amount</th>
              <th>Oldest Dues</th>
              <th>Nature of Party</th>
              <th>Reminders</th>
              <th>Bank Account</th>
              <th>Payment Options</th>
              <th>Added On</th>
              <th>Task Details</th>
              <th>Status (Inline)</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id}>
                <td>{t.debtor_name}</td>
                <td>{t.promise_date}</td>
                <td>₹{t.promise_amount}</td>
                <td>{t.total_dues ? `₹${t.total_dues}` : "-"}</td>
                <td>{t.demand_amount ? `₹${t.demand_amount}` : "-"}</td>
                <td>{t.oldest_dues || "-"}</td>
                <td>{t.nature_of_party || "-"}</td>
                <td>{t.reminders || "-"}</td>
                <td>{t.bank_account || "-"}</td>
                <td>{t.payment_options || "-"}</td>
                <td>
                  {t.added_on
                    ? new Date(t.added_on).toLocaleDateString()
                    : "-"}
                </td>
                <td>{t.task_details || "-"}</td>
                <td style={{ minWidth: "160px" }}>
                  <select
                    className="form-select form-select-sm"
                    value={t.status || "Pending"}
                    disabled={savingStatusId === t.id}
                    onChange={(e) =>
                      handleInlineStatusChange(t.id, e.target.value)
                    }
                  >
                    <option value="Pending">Pending</option>
                    <option value="Completed">Completed</option>
                    <option value="Follow-up">Follow-up</option>
                  </select>
                  {savingStatusId === t.id && (
                    <small className="text-muted d-block mt-1">
                      Saving...
                    </small>
                  )}
                </td>
              </tr>
            ))}
            {tasks.length === 0 && (
              <tr>
                <td colSpan={13} className="text-center text-muted">
                  No tasks found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Task Modal */}
      {showModal && (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Task</h5>
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-close"
                />
              </div>
              <div className="modal-body">
                {error && (
                  <div className="ac-alert ac-alert-danger mb-2">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="ac-alert ac-alert-success mb-2">
                    {success}
                  </div>
                )}

                {/* Debtor & Amount Section */}
                <div className="ac-section">
                  <h4>Debtor & Amount</h4>
                  <div className="ac-grid">
                    <div className="ac-field">
                      <label>Debtor</label>
                      <select
                        className="form-select"
                        value={newTask.debitor_id}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            debitor_id: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Debtor</option>
                        {debitors.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name} ({d.code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="ac-field">
                      <label>₹ Promise to Pay</label>
                      <input
                        className="form-control"
                        value={newTask.promise_amount}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            promise_amount: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="ac-field">
                      <label>Promise Date</label>
                      <input
                        className="form-control"
                        type="date"
                        value={newTask.promise_date}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            promise_date: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="ac-field">
                      <label>₹ Total Dues</label>
                      <input
                        className="form-control"
                        value={newTask.total_dues}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            total_dues: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="ac-field">
                      <label>₹ Demand Amount</label>
                      <input
                        className="form-control"
                        value={newTask.demand_amount}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            demand_amount: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Payment & Party Info */}
                <div className="ac-section">
                  <h4>Payment & Party Info</h4>
                  <div className="ac-grid">
                    <div className="ac-field">
                      <label>Oldest Dues</label>
                      <select
                        className="form-select"
                        value={newTask.oldest_dues}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            oldest_dues: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Oldest Dues</option>
                        <option value="30 DAYS">30 DAYS</option>
                        <option value="45 DAYS">45 DAYS</option>
                        <option value="60 DAYS">60 DAYS</option>
                        <option value="90 DAYS">90 DAYS</option>
                        <option value="BAD DEBTS">BAD DEBTS</option>
                      </select>
                    </div>

                    <div className="ac-field">
                      <label>Nature of Party</label>
                      <select
                        className="form-select"
                        value={newTask.nature_of_party}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            nature_of_party: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Nature of Party</option>
                        <option value="DUE">DUE</option>
                        <option value="ADVANCE">ADVANCE</option>
                        <option value="LATE PAYMENT">LATE PAYMENT</option>
                        <option value="QUICK PAYMENT">QUICK PAYMENT</option>
                        <option value="COUNTER">COUNTER</option>
                        <option value="DND">DND</option>
                      </select>
                    </div>

                    <div className="ac-field">
                      <label>Reminders</label>
                      <select
                        className="form-select"
                        value={newTask.reminders}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            reminders: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Reminder</option>
                        <option value="CALL AND MSG">CALL AND MSG</option>
                        <option value="ONLY MSG">ONLY MSG</option>
                        <option value="CALL 3 TIMES">CALL 3 TIMES</option>
                      </select>
                    </div>

                    <div className="ac-field">
                      <label>Bank Account Number</label>
                      <input
                        className="form-control"
                        placeholder="Optional"
                        value={newTask.bank_account}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            bank_account: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="ac-field">
                      <label>Payment Options</label>
                      <select
                        className="form-select"
                        value={newTask.payment_options}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            payment_options: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Payment Option</option>
                        <option value="UPI SCANNER">UPI SCANNER</option>
                        <option value="PHONE PAY NUMBER">
                          PHONE PAY NUMBER
                        </option>
                        <option value="NEFT/RTGS">NEFT/RTGS</option>
                        <option value="GRAHAK SEVA KENDRA ( CSP )">
                          GRAHAK SEVA KENDRA ( CSP )
                        </option>
                        <option value="CASH DEPOSIT IN MAIN BRANCH">
                          CASH DEPOSIT IN MAIN BRANCH
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Task & Status */}
                <div className="ac-section">
                  <h4>Task & Status</h4>
                  <div className="ac-grid">
                    <div className="ac-field" style={{ gridColumn: "1 / -1" }}>
                      <label>Task Details (optional)</label>
                      <textarea
                        className="form-control"
                        value={newTask.task_details}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            task_details: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="ac-field">
                      <label>Status</label>
                      <select
                        className="form-select"
                        value={newTask.status}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            status: e.target.value,
                          })
                        }
                      >
                        <option value="Pending">Pending</option>
                        <option value="Completed">Completed</option>
                        <option value="Follow-up">Follow-up</option>
                      </select>
                    </div>
                  </div>
                </div>

              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSubmit}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingTasks;